import { useState, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { getTimeZones, type TimeZone } from "@vvo/tzdb";
import { useOnboardingStore } from "../../store/onboardingSlice";
import { checkSlugAvailability, saveOnboardingStep } from "../../api/auth";
import { registerNewBusiness } from "../../api/auth";
import { useAuth } from "../../context/AuthContext";
import { config } from "../../config";
import { trackOnboardingStepComplete, trackSignUp } from "../../lib/analytics";

const TIMEZONES = getTimeZones().map((tz: TimeZone) => tz.name).sort();

const COUNTRY_CURRENCY_OPTIONS = [
  { countryCode: "AU", countryName: "Australia", currency: "AUD" },
  { countryCode: "CA", countryName: "Canada", currency: "CAD" },
  { countryCode: "GB", countryName: "United Kingdom", currency: "GBP" },
  { countryCode: "IE", countryName: "Ireland", currency: "EUR" },
  { countryCode: "NZ", countryName: "New Zealand", currency: "NZD" },
  { countryCode: "SG", countryName: "Singapore", currency: "SGD" },
  { countryCode: "US", countryName: "United States", currency: "USD" },
] as const;

const ENABLE_SHOPIFY = config.featureFlags.enableShopify;
const ENABLE_CLINIKO = config.featureFlags.enableCliniko;

const BUSINESS_MODES = [
  { value: "service", label: "Service business" },
  ...(ENABLE_SHOPIFY ? [{ value: "shop", label: "Online store (Shopify)" }] : []),
  ...(ENABLE_CLINIKO ? [{ value: "cliniko", label: "Cliniko (appointments)" }] : []),
];

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export default function BusinessBasicsStep() {
  const { setCurrentStep, setStepData, setSaving, saving } = useOnboardingStore();
  const { setBusinessId, currentUser } = useAuth();

  const [fields, setFields] = useState({
    business_name: "",
    slug: "",
    address: "",
    phone: "",
    description: "",
    business_mode: "service",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    country_code: "AU",
    currency: "AUD",
  });

  const [slugState, setSlugState] = useState<{
    checking: boolean;
    available: boolean | null;
    reason?: string;
  }>({ checking: false, available: null });

  const [error, setError] = useState<string | null>(null);

  const handleNameChange = (value: string) => {
    setFields((f) => ({
      ...f,
      business_name: value,
      slug: slugify(value).slice(0, 40),
    }));
    setSlugState({ checking: false, available: null });
  };

  const handleSlugChange = (value: string) => {
    setFields((f) => ({ ...f, slug: slugify(value).slice(0, 40) }));
    setSlugState({ checking: false, available: null });
  };

  const checkSlug = useCallback(async () => {
    if (!fields.slug || fields.slug.length < 3) return;
    setSlugState({ checking: true, available: null });
    try {
      const result = await checkSlugAvailability(fields.slug);
      setSlugState({ checking: false, available: result.available, reason: result.reason });
    } catch {
      setSlugState({ checking: false, available: null });
    }
  }, [fields.slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (slugState.available === false) return;

    setError(null);
    setSaving(true);

    try {
      const result = await registerNewBusiness({
        business_name: fields.business_name,
        business_mode: fields.business_mode,
        address: fields.address,
        phone: fields.phone,
        description: fields.description,
        slug: fields.slug,
        timezone: fields.timezone,
        country_code: fields.country_code,
        currency: fields.currency,
        email: currentUser?.email ?? undefined,
      });
      setBusinessId(result.business_id);
      setStepData("businessBasics", fields);
      await saveOnboardingStep({ step: "businessBasics", step_status: "complete" });
      trackSignUp();
      trackOnboardingStepComplete(1, "business_basics", true);
      setCurrentStep(2);
    } catch (err: unknown) {
      const normalized = err as { message?: string; data?: { error?: string } };
      setError(normalized?.data?.error ?? normalized?.message ?? "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h4" sx={{ mb: 1 }}>Tell us about your business</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        This information helps customers find and book with you.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TextField
        label="Business name"
        fullWidth
        required
        value={fields.business_name}
        onChange={(e) => handleNameChange(e.target.value)}
        sx={{ mb: 2 }}
      />

      <TextField
        label="URL slug"
        fullWidth
        required
        value={fields.slug}
        onChange={(e) => handleSlugChange(e.target.value)}
        onBlur={checkSlug}
        helperText={
          slugState.checking
            ? "Checking availability…"
            : slugState.available === true
            ? "✓ Available"
            : slugState.available === false
            ? `Not available${slugState.reason === "taken" ? " — try another" : ""}`
            : `Your booking page URL: ${config.bookingPageBaseUrl}/{slug}`
        }
        error={slugState.available === false}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {config.bookingPageBaseUrl}/
            </InputAdornment>
          ),
          endAdornment: slugState.checking ? <CircularProgress size={16} /> : null,
        }}
        sx={{ mb: 2 }}
      />

      <TextField
        label="Address"
        fullWidth
        required
        value={fields.address}
        onChange={(e) => setFields((f) => ({ ...f, address: e.target.value }))}
        sx={{ mb: 2 }}
      />

      <TextField
        label="Phone"
        fullWidth
        required
        value={fields.phone}
        onChange={(e) => setFields((f) => ({ ...f, phone: e.target.value }))}
        sx={{ mb: 2 }}
      />

      <TextField
        label="Description"
        fullWidth
        required
        multiline
        minRows={3}
        value={fields.description}
        onChange={(e) => setFields((f) => ({ ...f, description: e.target.value }))}
        sx={{ mb: 2 }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Business type</InputLabel>
        <Select
          value={fields.business_mode}
          label="Business type"
          onChange={(e) => setFields((f) => ({ ...f, business_mode: e.target.value }))}
        >
          {BUSINESS_MODES.map((m) => (
            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Timezone</InputLabel>
        <Select
          value={fields.timezone}
          label="Timezone"
          onChange={(e) => setFields((f) => ({ ...f, timezone: e.target.value }))}
        >
          {TIMEZONES.map((tz: string) => (
            <MenuItem key={tz} value={tz}>{tz}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Country</InputLabel>
        <Select
          value={fields.country_code}
          label="Country"
          onChange={(e) => {
            const countryCode = e.target.value;
            const option = COUNTRY_CURRENCY_OPTIONS.find((item) => item.countryCode === countryCode);
            setFields((f) => ({
              ...f,
              country_code: countryCode,
              currency: option?.currency ?? f.currency,
            }));
          }}
        >
          {COUNTRY_CURRENCY_OPTIONS.map((option) => (
            <MenuItem key={option.countryCode} value={option.countryCode}>
              {option.countryName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Currency</InputLabel>
        <Select
          value={fields.currency}
          label="Currency"
          disabled
        >
          <MenuItem value={fields.currency}>{fields.currency}</MenuItem>
        </Select>
      </FormControl>

      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={saving || slugState.available === false || !fields.business_name || !fields.slug}
      >
        {saving ? <CircularProgress size={22} color="inherit" /> : "Continue"}
      </Button>
    </Box>
  );
}
